<template>
  <div>
    <input
      :id="id"
      :class="className"
      type="file"
      @change="uploadFile"
      :accept="accept"
      :capture="capture"
      :multiple="multiple"
      :hidden="hidden"
    />
    <slot name="upload-label"></slot>
  </div>
</template>

<script>
/**
 * vue-ImageUploader: a to-the-point vue-component for client-side image upload with resizing of images (JPG, PNG, GIF)
 *
 * Code based on ImageUploader (c) Ross Turner (https://github.com/rossturner/HTML5-ImageUploader) and
 * exif.js (https://github.com/exif-js/exif-js) for JPEG autoRotate functions
 * Adapted for Vue by Svale Fossåskaret / Kartoteket with some modifications.
 *
 *
 * TODO Items:
 * 1. Progress Report
 * 2. Multiple Files / async handling
 * 3. Support custom completion callback
 * 4. Propper unit testing with https://github.com/visionmedia/supertest
 *
 * LICENSE (from original ImageUploader files by Ross Turner):
 *
 * Copyright (c) 2012 Ross Turner and contributors (https://github.com/zsinj)
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 **/
import dataURLtoBlob from "blueimp-canvas-to-blob";

export default {
  name: "image-uploader",
  props: {
    id: {
      type: String,
      default: "fileInput",
    },
    maxWidth: {
      type: Number,
      default: 1600,
    },
    maxHeight: {
      type: Number,
      default: 1600,
    },
    quality: {
      type: Number,
      default: 1.0,
    },
    outputFormat: {
      type: String,
      default: "base64",
    },
    className: {
      type: [String, Array],
      default: "form-control",
    },
    capture: {
      type: [String, Boolean],
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: "image/*",
    },
    hidden: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      imagePreview: null,
      allOriginalFile: [],
      allFiles: [],
    };
  },
  methods: {
    trigger() {
      document.getElementById(this.id).click();
    },
    uploadFile(e) {
      if (e.target.files && e.target.files.length) {
        for (let file of e.target.files) {
          this.allOriginalFile.push(file);
        }
        this.emitLoad();
        for (let index in this.allOriginalFile) {
          this.handleFile(parseInt(index));
        }
      }
    },
    emitLoad() {
      this.$emit("onUpload");
    },
    emitComplete() {
      this.allFiles = this.allFiles.sort(function(a, b){return a.my_position - b.my_position});
      this.$emit("onComplete", this.allFiles);
      this.allOriginalFile = [];
      this.allFiles = [];
      document.getElementById(this.id).value = null;
    },
    handleFile(index) {
      const that = this;
      const img = document.createElement("img");
      const reader = new window.FileReader();
      reader.onload = function (e) {
        img.src = e.target.result;
        img.onload = function () {
          that.scaleImage(img, index);
        };
      };
      reader.readAsDataURL(this.allOriginalFile[index]);
    },
    scaleImage(img, index) {
      let canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.save();
      ctx.drawImage(img, 0, 0);
      ctx.restore();
      const ratio = canvas.width / canvas.height;
      let mWidth = Math.min(this.maxWidth, ratio * this.maxHeight);
      if (mWidth <= 0) {
        mWidth = 1;
      }
      while (canvas.width >= 2 * mWidth) {
        canvas = this.getHalfScaleCanvas(canvas);
      }
      if (canvas.width > mWidth) {
        canvas = this.scaleCanvasWithAlgorithm(canvas, mWidth);
      }
      var quality = 0.8

      if (this.allOriginalFile[index].type === "image/jpeg"){
        this.allOriginalFile[index].type === "image/jpeg" ? this.quality : 1.0;
      }else if (this.allOriginalFile[index].type === "image/png"){
        this.allOriginalFile[index].type === "image/png" ? this.quality : 1.0;
      }
      const imageData = canvas.toDataURL(
        this.allOriginalFile[index].type,
        quality
      );
      if (typeof this.onScale === "function") {
        this.onScale(imageData);
      }
      this.allFiles.push({
        img: this.formatOutput(imageData),
        name: this.allOriginalFile[index].name,
        my_position: index
      });

      if (this.allFiles.length === this.allOriginalFile.length) {
        this.emitComplete();
      }
    },
    scaleCanvasWithAlgorithm(canvas, maxWidth) {
      const scaledCanvas = document.createElement("canvas");
      const scale = maxWidth / canvas.width;
      scaledCanvas.width = canvas.width * scale;
      scaledCanvas.height = canvas.height * scale;
      const srcImgData = canvas
        .getContext("2d")
        .getImageData(0, 0, canvas.width, canvas.height);
      const destImgData = scaledCanvas
        .getContext("2d")
        .createImageData(scaledCanvas.width, scaledCanvas.height);
      this.applyBilinearInterpolation(srcImgData, destImgData, scale);
      scaledCanvas.getContext("2d").putImageData(destImgData, 0, 0);
      return scaledCanvas;
    },
    applyBilinearInterpolation(srcCanvasData, destCanvasData, scale) {
      function inner(f00, f10, f01, f11, x, y) {
        const un_x = 1.0 - x;
        const un_y = 1.0 - y;
        return (
          f00 * un_x * un_y + f10 * x * un_y + f01 * un_x * y + f11 * x * y
        );
      }
      let i, j;
      let iyv, iy0, iy1, ixv, ix0, ix1;
      let idxD, idxS00, idxS10, idxS01, idxS11;
      let dx, dy;
      let r, g, b, a;
      for (i = 0; i < destCanvasData.height; ++i) {
        iyv = i / scale;
        iy0 = Math.floor(iyv);
        // Math.ceil can go over bounds
        iy1 =
          Math.ceil(iyv) > srcCanvasData.height - 1
            ? srcCanvasData.height - 1
            : Math.ceil(iyv);
        for (j = 0; j < destCanvasData.width; ++j) {
          ixv = j / scale;
          ix0 = Math.floor(ixv);
          // Math.ceil can go over bounds
          ix1 =
            Math.ceil(ixv) > srcCanvasData.width - 1
              ? srcCanvasData.width - 1
              : Math.ceil(ixv);
          idxD = (j + destCanvasData.width * i) * 4;
          // matrix to vector indices
          idxS00 = (ix0 + srcCanvasData.width * iy0) * 4;
          idxS10 = (ix1 + srcCanvasData.width * iy0) * 4;
          idxS01 = (ix0 + srcCanvasData.width * iy1) * 4;
          idxS11 = (ix1 + srcCanvasData.width * iy1) * 4;
          // overall coordinates to unit square
          dx = ixv - ix0;
          dy = iyv - iy0;
          // I let the r, g, b, a on purpose for debugging
          r = inner(
            srcCanvasData.data[idxS00],
            srcCanvasData.data[idxS10],
            srcCanvasData.data[idxS01],
            srcCanvasData.data[idxS11],
            dx,
            dy
          );
          destCanvasData.data[idxD] = r;
          g = inner(
            srcCanvasData.data[idxS00 + 1],
            srcCanvasData.data[idxS10 + 1],
            srcCanvasData.data[idxS01 + 1],
            srcCanvasData.data[idxS11 + 1],
            dx,
            dy
          );
          destCanvasData.data[idxD + 1] = g;
          b = inner(
            srcCanvasData.data[idxS00 + 2],
            srcCanvasData.data[idxS10 + 2],
            srcCanvasData.data[idxS01 + 2],
            srcCanvasData.data[idxS11 + 2],
            dx,
            dy
          );
          destCanvasData.data[idxD + 2] = b;
          a = inner(
            srcCanvasData.data[idxS00 + 3],
            srcCanvasData.data[idxS10 + 3],
            srcCanvasData.data[idxS01 + 3],
            srcCanvasData.data[idxS11 + 3],
            dx,
            dy
          );
          destCanvasData.data[idxD + 3] = a;
        }
      }
    },
    getHalfScaleCanvas(canvas) {
      const halfCanvas = document.createElement("canvas");
      halfCanvas.width = canvas.width / 2;
      halfCanvas.height = canvas.height / 2;
      halfCanvas
        .getContext("2d")
        .drawImage(canvas, 0, 0, halfCanvas.width, halfCanvas.height);
      return halfCanvas;
    },
    formatOutput(imageData) {
      if (this.outputFormat === "blob") {
        if (typeof dataURLtoBlob === "undefined") {
          return imageData;
        }
        return URL.createObjectURL(dataURLtoBlob(imageData));
      }
      return imageData;
    },
  },
};
</script>